// Layout Component
// =================
// Provides consistent layout with sidebar (desktop) and bottom nav (mobile)
// Feature #133: Added accessibility improvements (role attributes)
// Feature #278: Updated to use warm-gray canvas background with frame effect
// Feature #292: Updated mobile bottom nav with new design language

import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import KeyboardShortcutsOverlay, {
  useKeyboardShortcuts,
} from '../ui/KeyboardShortcuts';
import FeedbackButton from '../ui/FeedbackButton';

interface LayoutProps {
  children: React.ReactNode;
  /** Hide the layout navigation (e.g., for session pages) */
  hideNav?: boolean;
}

// Mobile bottom navigation items — mirrors desktop sidebar navKeys
const mobileNavItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: '🏠' },
  { path: '/sessions', labelKey: 'nav.mySessions', icon: '🎓' },
  { path: '/library', labelKey: 'nav.library', icon: '📚' },
  { path: '/preparation', labelKey: 'nav.preparation', icon: '📝' },
  { path: '/settings', labelKey: 'nav.settings', icon: '⚙️' },
];

const Layout: React.FC<LayoutProps> = ({ children, hideNav = false }) => {
  const { t } = useTranslation('common');
  const { showShortcuts, setShowShortcuts } = useKeyboardShortcuts();

  if (hideNav) {
    // Full-screen layout without navigation (for session pages, etc.)
    return (
      <>
        {children}
        <KeyboardShortcutsOverlay
          isOpen={showShortcuts}
          onClose={() => setShowShortcuts(false)}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {/* Keyboard Shortcuts Overlay */}
      <KeyboardShortcutsOverlay
        isOpen={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* Desktop Sidebar */}
      <Sidebar />

      {/* Main Content Area - offset by sidebar width on desktop */}
      {/* Feature #278: Added padding for 'frame effect' - content floats on canvas */}
      <div className="md:pl-64">
        {/* Page content with subtle padding for visual breathing room */}
        <main role="main" className="pb-20 md:pb-4 min-h-screen">
          {children}
        </main>
      </div>

      {/* Feedback Button */}
      <FeedbackButton context="app" />

      {/* Mobile Bottom Navigation - Feature #292: Updated with new design language */}
      <nav
        role="navigation"
        aria-label="Mobil navigation"
        className="fixed bottom-0 left-0 right-0 bg-canvas dark:bg-canvas-dark border-t border-frame-border dark:border-frame-border-dark md:hidden z-40 safe-area-inset-bottom"
      >
        <div className="flex justify-around py-3 px-2">
          {mobileNavItems.map((item) => {
            const label = t(item.labelKey);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                aria-label={label}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                      : 'text-neutral-medium hover:text-neutral-dark dark:hover:text-white hover:bg-background-light dark:hover:bg-background-dark'
                  }`
                }
              >
                <span className="text-xl" aria-hidden="true">
                  {item.icon}
                </span>
                <span className="text-xs font-sans font-medium">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
